<template>
  <SbPopover
    ref="popover"
    v-bind="{
      isOpen,
      offset,
      placement,
      reference: referenceEl,
      usePortal,
      zIndex,
      vcoConfig: {
        capture: true,
      },
    }"
    @hide="context.closeMenu"
  >
    <SbDropdown
      :id="context.menuListId"
      :aria-activedescendant="activatedMenuItemId"
      class="sb-menu-list"
      :aria-labelledby="context.menuButtonId"
      :class="customClass"
      :title="title"
      @keydown="handleKeyDown"
    >
      <template v-if="items.length > 0 || $slots.top">
        <slot name="top" />
        <SbMenuListItems :items="items" />
      </template>
      <slot />
    </SbDropdown>
  </SbPopover>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import type { Placement } from '@popperjs/core'
import SbMenuListItems from './MenuListItems.vue'
import SbPopover from '../Popover'
import SbDropdown from '../Dropdown/SbDropdown.vue'
import { isVueComponent } from '../../utils'
import { useMenu } from './utils'
import type { MenuItem } from './types'

type MenuListProps = {
  /**
   * The list of items to be rendered
   */
  items?: MenuItem[]

  /**
   * Links to SbPopover offset property
   */
  offset?: [number, number]

  /**
   * Links to SbPopover placement property
   */
  placement?: Placement

  /**
   * Links to SbPopover reference property. If received a string, it will be used as a query selector. If received an Element, it will be used as a reference element. If received an Object, it will be used as a reference object.
   */
  reference?: string | Element | Object | null

  /**
   * Control if the popover should be rendered inside a portal
   */
  usePortal?: boolean

  /**
   * The z-index of the popover
   */
  zIndex?: number

  /**
   * Customize the dropdown class
   */
  customClass?: string | undefined

  /**
   * Menu list title
   */
  title?: string
}

const props = withDefaults(defineProps<MenuListProps>(), {
  items: () => [],
  offset: () => [0, 5],
  placement: 'bottom-end',
  reference: null,
  usePortal: false,
  zIndex: 200,
  customClass: undefined,
  title: undefined,
})

const emit = defineEmits<{
  (e: 'keydown', event: KeyboardEvent): void
}>()

const { context } = useMenu()

const activatedMenuItemId = computed(() => context.value.activatedMenuItemId)
const isOpen = computed(() => context.value.isOpen)
const referenceEl = computed(() => {
  if (props.reference) {
    return isVueComponent(props.reference)
      ? props.reference.$el
      : props.reference
  }
  return `#${context.value.menuButtonId}`
})

const handleKeyDown = (event: KeyboardEvent): void => {
  event.stopPropagation()
  const { closeMenu, handleKeydownOnList } = context.value

  handleKeydownOnList(event, {
    onEscape: () => closeMenu(),
    onKeydown: (event: KeyboardEvent) => emit('keydown', event),
    onClose: () => closeMenu(),
  })
}
</script>
